import { useEffect, useMemo, useRef, useState } from "react"
import { DeepPartial } from "react-hook-form"
import { useIsoLayoutEffect } from "shared/lib/react/useIsoLayoutEffect"

import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { ApiResponse } from "shared/types/ApiResponse"
import { FilePreview } from "shared/types/File"
import { RoomTypename } from "shared/types/Project"
import { FormProviderProps, FormRef } from "shared/types/ReactHookForm"
import { Room } from "shared/types/Room"

import { useAttachmentsQuery } from "entities/attachments/api/useAttachmentsQuery"
import { useProjectAttachmentUpload } from "entities/attachments/api/useProjectAttachmentUpload"
import { ClarificationRequestModel } from "entities/clarification-requests/types/ClarificationRequestModel"
import { useFloorOptions } from "entities/floors/model/useFloorOptions"
import { useProjectAttachmentsData } from "entities/projects/api/useProjectAttachmentsData"
import { useProjectOptions } from "entities/projects/model/useProjectOptions"
import { useProjectPositionOptions } from "entities/projects/model/useProjectPositionOptions"
import {
  RoomFormData,
  RoomFormState
} from "features/room-management/types/RoomFormData"
import { RoomFormModel } from "features/room-management/types/RoomFormModel"
import { RoomFormOptions } from "features/room-management/types/RoomFormOptions"
import {
  setPersistentRoomFormHintClosed,
  usePersistentRoomFormHintClosed
} from "widgets/room/room-form/model/persistent-room-form-hint-store"

interface UseRoomFormArgs {
  room?: Partial<Room>
  projectID?: string
  parentID?: string
  displayPosition?: number
  clarificationRequest?: ClarificationRequestModel | null
  isPending?: boolean

  formRef?: FormRef<RoomFormState, unknown, RoomFormData>
  formOptions?: RoomFormOptions

  onChange?: (formData: RoomFormState) => void
  onSubmit: (formData: RoomFormData) => Promise<ApiResponse<unknown>> | void
}

export function useRoomForm({
  room,
  projectID,
  parentID,
  displayPosition,
  clarificationRequest,
  isPending,
  formRef: propsFormRef,
  formOptions,
  onSubmit,
  onChange
}: UseRoomFormArgs): RoomFormModel {
  const crContent =
    clarificationRequest?.data.clarificationRequest?.data?.content ?? null

  const defaultFormData = useMemo<DeepPartial<RoomFormData>>(
    () => ({
      room,
      parentID,
      projectID,
      displayPosition: displayPosition ?? 0,
      clarificationRequest: { content: crContent }
    }),
    [room, parentID, projectID, displayPosition, crContent]
  )

  const formRef = useRef<FormProviderProps<
    RoomFormState,
    unknown,
    RoomFormData
  > | null>(null)
  const [formData, setFormData] = useState<RoomFormState>(defaultFormData)

  useEffect(() => {
    return formRef.current?.subscribe({
      callback: ({ values }) => {
        setFormData(values)
      },
      formState: { values: true }
    })
  })

  const mergedRef = useMergedRefs<
    FormProviderProps<RoomFormState, unknown, RoomFormData>
  >(propsFormRef, formRef)

  const { options: projectOptions, isLoading: isLoadingProjectOptions } =
    useProjectOptions()

  const { options: floorOptions, isLoading: isLoadingFloorOptions } =
    useFloorOptions(formData.projectID || projectID || null)

  const { positionOptions, isLoading: isLoadingPositionOptions } =
    useProjectPositionOptions({
      childID: room?.id,
      projectID: formData.projectID || null,
      parentID: formData.parentID,
      childType: RoomTypename
    })

  const effectiveProjectID = formData.projectID || projectID || ""

  const { attachments, attachmentCategories } = useProjectAttachmentsData({
    projectID: effectiveProjectID,
    options: { skip: !effectiveProjectID }
  })

  const { createAttachment } = useProjectAttachmentUpload()

  const { data: linkedAttachmentsData } = useAttachmentsQuery({
    id: room?.id ?? "",
    resourceType: RoomTypename,
    skip: !room?.id || isPending
  })

  const handleUpload = useEvent((file: FilePreview, category: string) => {
    return createAttachment(file.file, {
      projectID: effectiveProjectID,
      category
    })
  })

  const isHintClosed = usePersistentRoomFormHintClosed()

  const handleHintClose = useEvent(() => {
    setPersistentRoomFormHintClosed(true)
  })

  useIsoLayoutEffect(() => {
    formRef.current?.reset(defaultFormData, {
      keepDirty: true,
      keepDirtyValues: true
    })
  }, [defaultFormData])

  useEffect(() => {
    if (formData.projectID !== projectID) {
      formRef.current?.setValue("parentID", formData.projectID)
    }
  }, [formData.projectID])

  useEffect(() => {
    if (formData.parentID !== parentID) {
      formRef.current?.setValue("displayPosition", 0)
    }
  }, [formData.parentID])

  return {
    defaultFormData,
    clarificationRequest,
    projectOptions,
    isLoadingProjectOptions,
    floorOptions,
    isLoadingFloorOptions,
    formRef: mergedRef,
    formOptions,

    positionOptions,
    isLoadingPositionOptions,

    attachments: attachments?.data ?? null,
    linkedAttachments: linkedAttachmentsData?.attachments ?? null,
    attachmentCategories: attachmentCategories?.data ?? null,
    onUpload: handleUpload,

    shouldShowHint: !isHintClosed,
    onHintClose: handleHintClose,
    onSubmit,
    onChange
  }
}
